import React, { useState, useRef, useEffect } from 'react';
import { Typography, Box, TextField, Paper, List, ListItem, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Search, ChevronDown } from 'lucide-react';
import { FORM_STYLES } from '../../styles/formStyles';

interface SearchableDropdownProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  searchPlaceholder?: string;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  fullWidth = true,
  searchPlaceholder = "Sök..."
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get display value
  const selectedOption = options.find(option => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleOptionSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm('');
    }
  };

  if (disabled) {
    return (
      <Box sx={{ flex: fullWidth ? 1 : 'none' }}>
        <Typography sx={FORM_STYLES.label}>
          {t(label)}
        </Typography>
        <Box sx={{
          backgroundColor: '#F9FCFF',
          border: '2px solid #E2E8F0',
          borderRadius: { xs: '10px', md: '12px' },
          padding: { xs: '10px 14px', md: '12px 16px' },
          fontSize: { xs: '14px', md: '15px' },
          fontWeight: 500,
          color: '#A0AEC0',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          minHeight: { xs: '40px', md: '44px' },
          display: 'flex',
          alignItems: 'center',
          opacity: 0.6
        }}>
          —
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: fullWidth ? 1 : 'none', position: 'relative' }} ref={dropdownRef}>
      <Typography sx={FORM_STYLES.label}>
        {t(label)}
      </Typography>
      
      {/* Dropdown trigger */}
      <Box
        onClick={handleToggleDropdown}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '2px solid #E2E8F0',
          borderRadius: { xs: '10px', md: '12px' },
          padding: { xs: '10px 14px', md: '12px 16px' },
          fontSize: { xs: '14px', md: '15px' },
          fontWeight: 500,
          color: value ? '#2D3748' : '#A0AEC0',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          minHeight: { xs: '40px', md: '44px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#CBD5E0',
            backgroundColor: '#F9FCFF'
          },
          '&:focus': {
            outline: 'none',
            borderColor: '#3182CE',
            boxShadow: '0 0 0 3px rgba(49, 130, 206, 0.1)'
          }
        }}
      >
        <span>{displayValue || placeholder}</span>
        <ChevronDown 
          size={16} 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }} 
        />
      </Box>

      {/* Dropdown menu */}
      {isOpen && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            mt: 1,
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            maxHeight: '300px',
            overflow: 'hidden'
          }}
        >
          {/* Search input */}
          <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0' }}>
            <TextField
              ref={searchInputRef}
              fullWidth
              size="small"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search size={16} style={{ marginRight: 8, color: '#A0AEC0' }} />,
                sx: {
                  fontSize: '14px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#E2E8F0'
                    },
                    '&:hover fieldset': {
                      borderColor: '#CBD5E0'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3182CE'
                    }
                  }
                }
              }}
            />
          </Box>

          {/* Options list */}
          <List sx={{ maxHeight: '200px', overflow: 'auto', p: 0 }}>
            {filteredOptions.length === 0 ? (
              <ListItem sx={{ py: 2, textAlign: 'center' }}>
                <ListItemText 
                  primary="Inga resultat hittades" 
                  sx={{ 
                    '& .MuiListItemText-primary': { 
                      fontSize: '14px', 
                      color: '#A0AEC0',
                      fontStyle: 'italic'
                    } 
                  }} 
                />
              </ListItem>
            ) : (
              filteredOptions.map((option) => (
                <ListItem
                  key={option.value}
                  onClick={() => !option.disabled && handleOptionSelect(option.value)}
                  sx={{
                    cursor: option.disabled ? 'not-allowed' : 'pointer',
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: option.disabled ? 'transparent' : '#F9FCFF'
                    },
                                          backgroundColor: value === option.value ? '#E3F2FD' : 'transparent',
                    borderLeft: value === option.value ? '3px solid #3182CE' : '3px solid transparent'
                  }}
                >
                  <ListItemText 
                    primary={t(option.label)}
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontSize: '14px',
                        fontWeight: value === option.value ? 600 : 500,
                        color: option.disabled ? '#A0AEC0' : '#2D3748'
                      } 
                    }} 
                  />
                </ListItem>
              ))
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
}; 